import type { Project } from '../types/executive'

export const projects: Project[] = [
  {
    name: 'Kaiser Permanente Los Angeles Medical Center',
    location: 'Los Angeles, CA — Sunset Blvd Campus',
    sector: 'Healthcare',
    value: '$410M',
    facts: ['460 Acute-Care Beds', '1,000,000 SF Campus', 'CMAR'],
    role: 'Project Executive',
    description:
      'Ground-up replacement of Kaiser Permanente’s Los Angeles Medical Center, delivering 460 new acute-care beds and comprehensive clinical facilities across an active campus. Required sequential OSHPD/HCAI state inspections, strict California seismic compliance, and phasing that kept the adjacent hospital fully operational with no clinical downtime or utility interruptions.',
    image:
      'https://cdn.magicpatterns.com/uploads/ii1a6NTV5aymx2tD8ZuzZs/Kaiser_Sunset.jpg',
  },
  {
    name: 'Providence Breeze Cancer Center',
    location: 'Providence Little Company of Mary',
    sector: 'Healthcare',
    value: '$80M',
    facts: ['108,000 SF', 'Five-Level Parking', 'CMAR'],
    role: 'Vice President / PX',
    description:
      'Outpatient facility providing advanced cancer care, including a five-level parking structure. Among the first projects awarded after Conroy became Vice President of Healthcare for Snyder Langston, where he grew the backlog from $0 to $120M.',
    image:
      'https://cdn.magicpatterns.com/uploads/uENz2CRyjFu96xuBL5mS2S/Providence_Breeze_Cancer_Center.jpg',
  },
  {
    name: 'Los Alamitos Medical Plaza',
    location: 'Los Alamitos, CA',
    sector: 'Healthcare',
    value: '$37M',
    facts: ['70,000 SF MOB', '1,077-Stall Parking', '14-Month Delivery'],
    role: 'Vice President / PX',
    description:
      'A three-story, 70,000 SF medical office building and six-level, 1,077-stall parking structure on an operating hospital campus, expanding physician office capacity. Delivered via CMAR/Design-Build on budget and on schedule in 14 months using complex logistics and phased construction.',
    image:
      'https://cdn.magicpatterns.com/uploads/pWQurejgBTZAxpqGJXcFKa/Los_Alamitos_Medical_Office_Building_and_Parking_Structure.jpg',
  },
  {
    name: 'LAX APM Terminal Cores',
    location: 'Los Angeles International Airport',
    sector: 'Aviation',
    value: '$490M',
    facts: ['3 Terminal Cores', 'Zero Critical Claims', 'Design-Build'],
    role: 'Vice President, Western Region',
    description:
      'New vertical circulation and access/egress space at Tom Bradley International, Terminal 5.5, and Terminal 7, connecting passengers to the Automated People Mover. Delivered landside-only with public access open 24/7 — part of a $585M aviation portfolio completed with zero critical claims, earning the ASCE California Outstanding Airport Project award and an ENR Best Project Award of Merit.',
    image:
      'https://cdn.magicpatterns.com/uploads/hcdenVQGHrFYaNpkGTeNdp/LAX-Cores-scaled.jpg',
  },
  {
    name: 'LAX Gateway Project',
    location: 'Los Angeles International Airport',
    sector: 'Aviation',
    value: '$120M',
    facts: ['15 Light Pylons', 'Design-Build', 'Multi-Agency'],
    role: 'Pursuit & Team Assembly Lead',
    description:
      'Design-Build reimagining of the LAX civic gateway — the Airport Traffic Control Tower, 15 illuminated pylons, and monumental signage — coordinated across LADBS, LADPW, LADOT, LABOE, Metro, Caltrans, the FAA, and the City of Los Angeles Department of Cultural Affairs.',
    image:
      'https://cdn.magicpatterns.com/uploads/1j3K8Fdos9Jhettts1DDSQ/LAX_Gateway_1.jpg',
  },
  {
    name: 'McCormick Place South Hall Expansion',
    location: 'Chicago, IL',
    sector: 'Civic',
    value: '$675M',
    facts: ['2.9 MSF', 'Joint Venture', 'Design-Build'],
    role: 'Design Management & Procurement',
    description:
      'One of the largest exhibition-hall expansions in the country, delivered by a Clark / Hunt / Morse Diesel joint venture. Responsible for design development, GMP assembly, and trade contractor procurement, with direct oversight of the concrete superstructure package. Recognized by the AIA, the Chicago Building Congress, and DBIA.',
    image:
      'https://cdn.magicpatterns.com/uploads/kpJHNF6UUJbKbEHkwbAYcB/McCormick_Place_South_Hall_Expansion.jpg',
  },
  {
    name: 'San Bernardino Justice Center',
    location: 'San Bernardino, CA',
    sector: 'Civic',
    value: '$339.8M',
    facts: ['383,745 SF', '11 Stories', '35 Courtrooms + 2 Hearing Rooms'],
    role: 'Public-Sector Competitive Pursuit Lead',
    description:
      'Led cross-functional estimating, scheduling, and engineering teams through a complex competitive pursuit, aligning design with budget requirements to secure the public-sector award for a 3-story linear podium and 11-story courtroom tower.',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/09055abf-ebe5-433b-90a4-2fa0f46a0fc4.jpg',
  },
  {
    name: 'Chicago Board of Trade — Eastern Addition',
    location: 'Chicago, IL',
    sector: 'Commercial',
    value: '$175M',
    facts: ['350,000 SF', '45MW N+1', '1994–1997'],
    role: 'Preconstruction Manager / Project Manager',
    description:
      'Led the design-build JV team through feasibility and cost development, breaking ground within four months of initial budgeting and presenting a GMP at 50% schematic design to the Chicago Board of Trade Board of Directors. The addition delivered what was then the largest trading floor in the world, backed by 45MW of N+1 power and blast-resistant construction.',
    image:
      'https://cdn.magicpatterns.com/uploads/6UNbB8xwjLGLuzK34tMSGU/MadisonRose_CBOT_5032-1.png',
  },
  {
    name: 'Campus Point',
    location: 'San Diego, CA',
    sector: 'Commercial',
    value: '$80M',
    facts: ['3 Buildings', 'Life Science Conversion', 'Joint Venture'],
    role: 'Principal-in-Charge',
    description:
      'Repositioned Campus Point Center in La Jolla from “B” occupancy into a life science campus for Phase 3 Real Estate Development, including new interiors, MEP, process piping, and dynamic glazing. Led development and execution of the JV agreement and overall project oversight.',
    image:
      'https://cdn.magicpatterns.com/uploads/j9kHXXcPkfp6Bn64WCo8vU/Campus_Point_2.jpg',
  },
  {
    name: 'Crate and Barrel Corporate Headquarters',
    location: 'Northbrook, IL',
    sector: 'Commercial',
    value: '$60M',
    facts: ['175,000 SF', 'CMAR', 'Schedule Recovery'],
    role: 'Senior Project Manager',
    description:
      'Corporate headquarters delivered via CMAR after Conroy was brought in to recover a schedule-challenged project, working full time on site for six months and developing the recovery schedule. Completed on schedule and free of contractor claims; received an AIA Chicago Honor Award.',
    image:
      'https://cdn.magicpatterns.com/uploads/8jjThP8JNup8ch7mfd6fKq/Crate_and_Barrel_Corporate_HQ_-_Northbrook,_IL.jpg',
  },
  {
    name: 'Playa Jefferson — Building E',
    location: 'Playa Vista, CA',
    sector: 'Commercial',
    value: '$15M',
    facts: ['50,000 SF', 'GMP Contract', 'Mixed-Use'],
    role: 'Business-Unit Leadership Oversight',
    description:
      'A 50,000 SF commercial building delivered for Paul Marshall and Associates within the Playa Jefferson mixed-use development under a Guaranteed Maximum Price contract. Received an ENR California Best Project award for Southern California in the Office/Retail/Mixed-Use category.',
    image:
      'https://cdn.magicpatterns.com/uploads/hckKywNz1XqLKSuiXv6Szo/Play_Jefferson_-.webp',
  },
]

export const projectSectors = ['All', 'Healthcare', 'Aviation', 'Civic', 'Commercial'] as const
